import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  LinearProgress,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
} from '@mui/material';
import { useAccount, useBalance, useContractWrite, useWaitForTransaction, useChainId, useContractRead } from 'wagmi';
import { CONTRACTS, CHAIN_ID, VOTE_COSTS, getTxUrl } from '../config/contracts';
import { SimpleGovernanceABI, FactRegistryABI } from '../abis';
import '../styles/retro-terminal.css';

interface Proposal {
  id: number;
  title: string;
  description: string;
  proposer: string;
  votesFor: number;
  votesAgainst: number;
  totalVotes: number;
  status: 'active' | 'passed' | 'failed';
  endTime: string;
  category: 'oracle' | 'governance' | 'treasury';
}

const GovernanceVoting: React.FC = () => {
  const { address, isConnected } = useAccount();
  const chainId = useChainId();
  const contracts = CONTRACTS[chainId as keyof typeof CONTRACTS] || CONTRACTS[CHAIN_ID.BASE_SEPOLIA];

  const { data: lexiBalance } = useBalance({
    address,
    token: contracts.LEXI_TOKEN,
  });

  const { data: hash, isLoading: isPending } = useContractWrite({
    address: CONTRACTS.SIMPLE_GOVERNANCE,
    abi: SimpleGovernanceABI,
    functionName: 'vote',
  });
  const { isLoading: isConfirming, isSuccess: isConfirmed } = useWaitForTransaction({ hash });

  const [selectedProposal, setSelectedProposal] = useState<Proposal | null>(null);
  const [voteDialogOpen, setVoteDialogOpen] = useState(false);
  const [selectedVote, setSelectedVote] = useState<'for' | 'against' | null>(null);
  const [isVoting] = useState(false);
  const [step] = useState<'idle' | 'approving' | 'voting' | 'success'>('idle');

  // Fetch live proposals from FactRegistry
  const { data: pendingProposalsData } = (useContractRead as any)({
    address: contracts.FACT_REGISTRY,
    abi: FactRegistryABI,
    functionName: 'getPendingProposals',
  });

  const { data: proposalCount } = useContractRead({
    address: contracts.FACT_REGISTRY,
    abi: FactRegistryABI,
    functionName: 'proposalCount',
  });

  const [proposals, setProposals] = useState<Proposal[]>([]);

  // Use proposalCount for display if available
  const totalProposals = proposals.length || (proposalCount ? Number(proposalCount) : 0);

  useEffect(() => {
    if (pendingProposalsData && Array.isArray(pendingProposalsData) && pendingProposalsData.length === 6) {
      const [proposalIds, proposers, oracleAddresses, keys, values, timestamps] = pendingProposalsData;
      const oracleConfigs = [
        { address: contracts.IDENTITY_ORACLE?.toLowerCase(), name: 'Identity' },
        { address: contracts.GEOGRAPHY_ORACLE?.toLowerCase(), name: 'Geography' },
        { address: contracts.PHILOSOPHY_ORACLE?.toLowerCase(), name: 'Philosophy' },
        { address: contracts.SCIENCE_ORACLE?.toLowerCase(), name: 'Science' },
        { address: contracts.HISTORY_ORACLE?.toLowerCase(), name: 'History' },
        { address: contracts.ARTS_ORACLE?.toLowerCase(), name: 'Arts' },
        { address: contracts.TECHNOLOGY_ORACLE?.toLowerCase(), name: 'Technology' },
      ];

      const facts = proposalIds.map((id: any, index: number) => {
        const oracle = oracleConfigs.find(o => o.address === oracleAddresses[index]?.toLowerCase());
        return {
          id: Number(id),
          title: `Add Fact: ${keys[index]}`,
          description: `Approve: "${values[index]}" to ${oracle?.name || 'Oracle'}`,
          proposer: proposers[index],
          votesFor: 0,
          votesAgainst: 0,
          totalVotes: 0,
          status: 'active' as const,
          endTime: new Date(Number(timestamps[index]) * 1000 + 3 * 24 * 60 * 60 * 1000).toISOString(),
          category: 'oracle' as const,
        };
      });
      setProposals(facts);
    } else {
      // Fallback to empty if no data
      setProposals([]);
    }
  }, [pendingProposalsData, contracts]);


  const handleVote = async () => {
    if (!selectedProposal || !selectedVote || !address) return;

    const balance = lexiBalance ? parseFloat(lexiBalance.formatted) : 0;
    if (balance < parseFloat(VOTE_COSTS.GOVERNANCE_VOTE)) {
      alert(`Insufficient LIVINGARCADE balance. You need at least ${VOTE_COSTS.GOVERNANCE_VOTE} LIVINGARCADE tokens to vote.`);
      return;
    }

    // TODO: Implement wagmi v2 writeContract pattern
    alert(`Voting functionality coming soon!\n\nTo implement:\n1. Approve ${VOTE_COSTS.GOVERNANCE_VOTE} LIVINGARCADE tokens\n2. Call vote() on SimpleGovernance\n\nGovernance contract: ${contracts.GOVERNANCE}\nYour vote: ${selectedVote === 'for' ? 'FOR' : 'AGAINST'}\nProposal: ${selectedProposal.title}`);
    setVoteDialogOpen(false);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'primary';
      case 'passed':
        return 'success';
      case 'failed':
        return 'error';
      default:
        return 'default';
    }
  };

  const getVoteProgress = (proposal: Proposal) => {
    const total = proposal.votesFor + proposal.votesAgainst;
    if (total === 0) return { for: 0, against: 0 };
    return {
      for: (proposal.votesFor / total) * 100,
      against: (proposal.votesAgainst / total) * 100,
    };
  };

  const getTimeRemaining = (endTime: string) => {
    const now = new Date();
    const end = new Date(endTime);
    const diff = end.getTime() - now.getTime();

    if (diff <= 0) return 'Voting Ended';

    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

    let timeString = '';
    if (days > 0) timeString += `${days}d `;
    if (hours > 0) timeString += `${hours}h `;
    if (minutes > 0) timeString += `${minutes}m`;

    return timeString.trim() || 'Less than a minute';
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ mb: 4, textAlign: 'center' }}>
        <Typography 
          variant="h4" 
          component="h1" 
          className="retro-terminal retro-terminal-text"
          sx={{ 
            fontFamily: "'Courier Prime', 'Courier New', monospace",
            fontSize: '24px',
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '2px',
            textShadow: '0 0 5px #00ff00',
            color: '#00ff00'
          }}
          gutterBottom
        >
          GOVERNANCE VOTING
        </Typography>
        <Typography 
          variant="h6" 
          className="retro-terminal retro-terminal-text"
          sx={{ 
            fontFamily: "'Courier Prime', 'Courier New', monospace",
            fontSize: '14px',
            opacity: 0.8,
            textTransform: 'uppercase',
            letterSpacing: '1px',
            color: '#00ff00'
          }}
          gutterBottom
        >
          DECENTRALIZED DECISION-MAKING
        </Typography>
      </Box>

      {!isConnected ? (
        <Alert severity="warning" sx={{ mb: 4 }}>
          Connect your wallet to participate in governance.
        </Alert>
      ) : (
        <>
          <Alert severity="info" sx={{ mb: 4 }}>
            <Typography variant="body2">
              <strong>Your LIVINGARCADE Balance:</strong> {lexiBalance ? parseFloat(lexiBalance.formatted).toFixed(2) : '0'} LIVINGARCADE
              <br />
              <strong>Active Proposals:</strong> {proposals.filter((p) => p.status === 'active').length}
              <br />
              <strong>Voting Cost:</strong> {VOTE_COSTS.GOVERNANCE_VOTE} LIVINGARCADE tokens per vote
              <br />
              <strong>Total Proposals:</strong> {totalProposals}
            </Typography>
          </Alert>

          <Grid container spacing={3}>
            {proposals.map((proposal) => {
              const voteProgress = getVoteProgress(proposal);

              return (
                <Grid item xs={12} md={6} key={proposal.id}>
                  <Card variant="outlined" sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <CardContent sx={{ flexGrow: 1 }}>
            <Typography 
              variant="overline" 
              className="retro-terminal retro-terminal-text"
              sx={{ 
                fontFamily: "'Courier Prime', 'Courier New', monospace",
                fontSize: '11px',
                opacity: 0.7,
                letterSpacing: '1px',
                color: '#00ff00'
              }}
            >
                FACT PROPOSAL #{proposal.id}
              </Typography>
                      <Typography 
                        variant="h6" 
                        className="retro-terminal retro-terminal-text"
                        sx={{ 
                          fontFamily: "'Courier Prime', 'Courier New', monospace",
                          fontSize: '16px',
                          fontWeight: 700,
                          letterSpacing: '1px',
                          color: '#00ff00',
                          textTransform: 'uppercase'
                        }}
                        gutterBottom
                      >
                        {proposal.title}
                      </Typography>
                      <Typography 
                        variant="body2" 
                        className="retro-terminal retro-terminal-text"
                        sx={{ 
                          mb: 2,
                          fontFamily: "'Courier Prime', 'Courier New', monospace",
                          fontSize: '13px',
                          opacity: 0.8,
                          color: '#00ff00'
                        }}
                      >
                        {proposal.description}
                      </Typography>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2">
                          <strong>Votes For:</strong> {proposal.votesFor} ({voteProgress.for.toFixed(1)}%)
                        </Typography>
                        <LinearProgress
                          variant="determinate"
                          value={voteProgress.for}
                          color="success"
                          sx={{ height: 8, borderRadius: 4, mb: 1 }}
                        />
                        <Typography variant="body2">
                          <strong>Votes Against:</strong> {proposal.votesAgainst} ({voteProgress.against.toFixed(1)}%)
                        </Typography>
                        <LinearProgress
                          variant="determinate"
                          value={voteProgress.against}
                          color="error"
                          sx={{ height: 8, borderRadius: 4 }}
                        />
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
                        <Chip label={proposal.status.toUpperCase()} color={getStatusColor(proposal.status)} size="small" />
                        <Typography variant="caption" color="text.secondary">
                          {getTimeRemaining(proposal.endTime)}
                        </Typography>
                      </Box>
                    </CardContent>

                    <CardActions>
                      {proposal.status === 'active' && (
                        <Button
                          variant="contained"
                          onClick={() => {
                            setSelectedProposal(proposal);
                            setVoteDialogOpen(true);
                          }}
                          disabled={!isConnected}
                          fullWidth
                        >
                          Vote ({VOTE_COSTS.GOVERNANCE_VOTE} LIVINGARCADE)
                        </Button>
                      )}
                      {proposal.status === 'passed' && (
                        <Button variant="outlined" fullWidth>
                          Execute
                        </Button>
                      )}
                    </CardActions>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </>
      )}

      {/* Voting Dialog */}
      <Dialog open={voteDialogOpen} onClose={() => !isVoting && setVoteDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Vote on Proposal</DialogTitle>
        <DialogContent>
          {selectedProposal && (
            <>
              <Typography variant="h6" gutterBottom>
                {selectedProposal.title}
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                {selectedProposal.description}
              </Typography>
              <Alert severity="info" sx={{ mb: 3 }}>
                <Typography variant="body2">
                  <strong>Voting Cost:</strong> {VOTE_COSTS.GOVERNANCE_VOTE} LIVINGARCADE tokens will be transferred
                  <br />
                  <strong>Your LIVINGARCADE Balance:</strong> {lexiBalance ? parseFloat(lexiBalance.formatted).toFixed(2) : '0'}{' '}
                  LIVINGARCADE
                </Typography>
              </Alert>
              <Typography variant="subtitle1" gutterBottom sx={{ mb: 2 }}>
                How do you vote on this fact?
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                <Button
                  variant={selectedVote === 'for' ? 'contained' : 'outlined'}
                  color="success"
                  size="large"
                  fullWidth
                  onClick={() => setSelectedVote('for')}
                  sx={{
                    py: 3,
                    fontSize: '1.2rem',
                    flexDirection: 'column',
                    gap: 1,
                  }}
                >
                  <span style={{ fontSize: '3rem' }}>👍</span>
                  <Typography variant="body1" fontWeight="bold">Approve</Typography>
                  <Typography variant="caption">This fact is accurate</Typography>
                </Button>
                <Button
                  variant={selectedVote === 'against' ? 'contained' : 'outlined'}
                  color="error"
                  size="large"
                  fullWidth
                  onClick={() => setSelectedVote('against')}
                  sx={{
                    py: 3,
                    fontSize: '1.2rem',
                    flexDirection: 'column',
                    gap: 1,
                  }}
                >
                  <span style={{ fontSize: '3rem' }}>👎</span>
                  <Typography variant="body1" fontWeight="bold">Reject</Typography>
                  <Typography variant="caption">This fact is inaccurate</Typography>
                </Button>
              </Box>
              {step !== 'idle' && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" color="primary">
                    {step === 'approving' && '⏳ Step 1/2: Approving LIVINGARCADE tokens...'}
                    {step === 'voting' && '⏳ Step 2/2: Casting vote on-chain...'}
                    {step === 'success' && '✅ Success! Vote recorded on blockchain.'}
                  </Typography>
                </Box>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setVoteDialogOpen(false)} disabled={isVoting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleVote}
            disabled={!selectedVote || isVoting || isPending || isConfirming}
          >
            {isVoting || isPending || isConfirming ? (
              <>
                <CircularProgress size={20} sx={{ mr: 1 }} />
                {step === 'approving' ? 'Approving...' : step === 'voting' ? 'Voting...' : 'Processing...'}
              </>
            ) : (
                `Submit Vote (${VOTE_COSTS.GOVERNANCE_VOTE} LIVINGARCADE)`
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Transaction Status */}
      {hash && (
        <Alert
          severity={isConfirmed ? 'success' : 'info'}
          sx={{ position: 'fixed', bottom: 20, right: 20, minWidth: 300 }}
        >
          <Typography variant="body2">
            {isConfirmed ? (
              <>
                <strong>Vote Submitted!</strong>
                <br />
                <a href={getTxUrl(chainId, hash)} target="_blank" rel="noopener noreferrer">
                  View on BaseScan ↗
                </a>
              </>
            ) : (
              <>
                <strong>Processing Vote...</strong>
                <br />
                Transaction: {hash.slice(0, 10)}...
              </>
            )}
          </Typography>
        </Alert>
      )}
    </Container>
  );
};

export default GovernanceVoting;
